/**
 *
 * @component 甘特图插件封装为React 组件
 *
 **/
import { gantt } from 'gantt_commercial';
import dayjs from 'dayjs';
import 'gantt_commercial/codebase/dhtmlxgantt.css';
import './index.scss';

const DEFAULT_CONFIG = {
    select_task: false,
    bar_height: 20, // sets the height of task bars in the timeline area    (full | int")
    scale_height: 41,
    row_height: 41,
    readonly: true,
    autofit: true, // 若设置为true则平均分布
    // grid_width
    layout: {
        css: 'gantt_container',
        rows: [
            {
                cols: [
                    {
                        view: 'grid',
                        id: 'grid',
                        scrollX: 'scrollHor',
                        scrollY: 'scrollVer',
                    },
                    { resizer: true, width: 1 },
                    {
                        view: 'timeline',
                        id: 'timeline',
                        scrollX: 'scrollHor',
                        scrollY: 'scrollVer',
                    },
                    { view: 'scrollbar', scroll: 'y', id: 'scrollVer' },
                ],
            },
            { view: 'scrollbar', scroll: 'x', id: 'scrollHor', height: 20 },
        ],
    },
};

const DEFAULT_TEMPLATES = {
    // task_class 各种状态任务条的CSS
    task_class(start, end, task) {
        let res = '';
        if (task.curProcess === -1) res = 'complete-type';
        if (task.curProcess === 0) res = 'not-begin-type';
        if (task.curProcess > 0 && task.curProcess <= task.period) {
            res = 'underway-type';
        }
        if (task.curProcess > task.period) res = 'overdue-type';
        return res;
    },
    tooltip_text(start, end, task) {
        return `<div>${intl.get('projectSpace.planLimit')} : ${dayjs(
            start
        ).format('YYYY/MM/DD')} - ${dayjs(end).format('YYYY/MM/DD')}</div>
            <div>${intl.get('gantt.currentProcess')} ${
            task.curProcess >= 0
                ? intl.get('gantt.day', { curProcess: task.curProcess })
                : ''
        }</div>
            <div>${intl.get('gantt.rest')} ${task.timeLeft}${intl.get(
            'gantt.days'
        )}</div>
            <div>${intl.get('gantt.postpone')} ${
            task.postponementTime
        }${intl.get('gantt.days')}</div>`;
    },
    //  任务条里的文本
    task_text(start, end, task) {
        return ' ';
    },
};

/**
 * @see {@link https://docs.dhtmlx.com/gantt/desktop__date_format.html}
 */
const SCALES = {
    day: [
        {
            unit: 'day',
            step: 1,
            format: '%y-%m-%d', // %d %l %j %D
            // css - (function) a function that returns the name of a CSS class that will be applied to the scale units. Takes a date object as a parameter.
            // css(date) {
            //     if (!gantt.isWorkTime({ date: date, unit: 'day' })) {
            //         return 'weekend';
            //     }
            // },
            // format: function (date) {
            //     return "Week #" + gantt.date.getWeek(date);
            // }
        },
    ],
    month: [
        { unit: 'month', step: 1, format: '%Y - %m' }, // %M %F %m %n
    ],
};

export default function BaseGantt(props) {
    const ganttContainer = useRef();
    const { tasks, columns, i18n, height } = props;

    // 初始化
    useEffect(() => {
        gantt.plugins({
            tooltip: true,
            marker: true,
            quick_info: false,
        });
        gantt.i18n.setLocale(i18n);
        gantt.addMarker({
            start_date: new Date(),
            text: intl.get('userCenter.today'),
        });
        Object.assign(gantt.config, DEFAULT_CONFIG, {
            columns,
            autosize: height === 'auto' ? 'y' : '',
            scales: SCALES[props.timeLine],
        });
        Object.assign(gantt.templates, DEFAULT_TEMPLATES);

        gantt.init(ganttContainer.current);
    }, [columns, i18n, height]);

    // 数据更新
    useEffect(() => {
        gantt.parse({
            tasks,
            // ...可拓展links
        });
        gantt.render();
    }, [tasks]);

    return (
        <div
            ref={ganttContainer}
            className="base-gantt"
            style={{ width: '100%', height: props.height }}
        />
    );
}

BaseGantt.defaultProps = {
    i18n: 'cn', // cn or 'en'
    height: 'auto', // auto or css unit
    timeLine: 'month', // month or day
};

/**
 *
 * @example gantt data
 *
 *
 * columns: [
 *  {
 *      name: 'text',
 *      width: '*',
 *      resize: true,
 *      template: function (task) {
 *          return task.label+'自定义后缀';
 *      },
 * },
 *  { name: 'start_date', label: '开始时间', align: 'center' },
 *  {
 *      name: 'duration', // name (string) defines the column's id. // duration的话 会自动计算天数
 *      label: 'Duration', // label (string) specifies the title of the column
 *      width: 60,
 *      align: 'center',
 *      onrender: function (task, node) {
 *           node.setAttribute('title', task.text);
 *       },
 *    },
 *    { name: 'add', label: '', width: 50, align: 'center' }, //  添加任务的columns
 *  ],
 * tasks: [
 *    {
 *        id: 2,
 *        text: 'Task #1',
 *        start_date: '02-04-2013',
 *        duration: 8,
 *        progress: 13 / 16,
 *        type: 'project',
 *        cust: 'ddddd',
 *        // parent: 1,
 *         tree: true,
 *    }
 * ],
 *
 *
 *
 *
 *  @example dayjs
 *
 *  dayjs('1970-00-00', 'YYYY-MM-DD').isValid()
 *  dayjs('1970-00-00', 'YYYY-MM-DD', true).isValid()
 *  dayjs('1970-00-00', 'YYYY-MM-DD', 'es', true).isValid()
 *  dayjs().isValid()
 *  dayjs('2019-01-25').add(1, 'day').subtract(1, 'year').year(2009).toString()
 *  dayjs('2019-01-25').toDate()
 *  dayjs('2019-01-25').format('[YYYYescape] YYYY-MM-DDTHH:mm:ssZ[Z]')
 *
 *
 */
